import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BalanceIcon from '@mui/icons-material/Balance';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventIcon from '@mui/icons-material/Event';
import GavelIcon from '@mui/icons-material/Gavel';
import MicIcon from '@mui/icons-material/Mic';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublicIcon from '@mui/icons-material/Public';
import StarIcon from '@mui/icons-material/Star';
import { GlobalStyles } from '@mui/material';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackScheduleCall, trackEvent } from '../components/GoogleAnalytics';
import JoinTheTeamDialog from '../components/JoinTheTeamDialog';
import SEO from '../components/SEO';
import { generateWebPageSchema } from '../utils/schemaGenerators';

// Import hero image and bill images (webp variants for smaller payloads)
import CustodyImage from '../assets/images/5050-custody.webp';
import AppealableOrdersImage from '../assets/images/appealable-temporary-orders.webp';
import CamerasImage from '../assets/images/cameras-in-courtrooms.webp';
import ChildSupportImage from '../assets/images/child-support-dual-calculation-reform.webp';
import IStandWithJohnImage from '../assets/images/i-stand-with-john-documentary.webp';
import AffiliateLogoThree from '../assets/images/logo-john-mast.webp';
import AffiliateLogoTwo from '../assets/images/logo-npo.webp';
import AffiliateLogoOne from '../assets/images/logo-texans-for-judicial-accountability.webp';
import HeroImage from '../assets/images/robert-garza-headshot-2.webp';
import ThreeStrikesImage from '../assets/images/three-strikes.webp';
import TimeTakenTimeBackImage from '../assets/images/time-taken-time-back.webp';
import WeTheParentsImage from '../assets/images/we-the-parents-documentary.webp';

// Design tokens: one accent (brand gold), locked across the whole page.
// Light mode text accents use a darkened gold for WCAG AA contrast (5.3:1 on white).
const ACCENT_LIGHT = '#8B6508';
const ACCENT_DARK = '#FFD700';

// Lazy load heavy components
const CalendlyDialog = React.lazy(() => import('../components/CalendlyDialog'));

interface SpeakingEngagement {
  event: string;
  role: string;
  date: string;
}

interface Publication {
  title: string;
  detail: string;
}

const speakingEngagements: SpeakingEngagement[] = [
  { event: 'Texas Commission on Marriage and Family', role: 'Testimony', date: 'August 18, 2026' },
  { event: 'Parental Equality Conference, Sofia, Bulgaria', role: 'Keynote Speaker', date: 'April 25-27, 2026' },
  { event: 'New York Shared Parenting Conference', role: 'Speaker', date: 'April 19, 2026' },
  { event: 'Solutions for Family Court Reform, Salt Lake City', role: 'Keynote Speaker', date: 'April 10-12, 2026' },
  { event: 'PASG 2025 Conference, Toronto', role: 'Featured Speaker', date: '2025' },
  {
    event: 'Parental Alienation & TFC 157.168 Training, Bell County Justice Complex',
    role: 'Training',
    date: '2024',
  },
  { event: 'Parental Alienation Training Conference, Austin TX', role: 'Training', date: '2024' },
  { event: 'Fatherlessness Round Table, CPAC, Dallas TX', role: 'Round Table', date: '2022' },
  { event: 'Fatherlessness Round Table, Washington D.C.', role: 'Round Table', date: '2022' },
  { event: 'Capitol Hill Advocacy Events', role: 'Advocacy', date: 'Ongoing' },
  { event: 'National & International Advocacy Summits', role: 'Summits', date: 'Ongoing' },
];

const publications: Publication[] = [
  {
    title: 'A Duty to Warn (2026)',
    detail: 'Featured advocate and contributor. Documentary on family court reform and judicial accountability.',
  },
  {
    title: 'The Divorced Dadvocate (2026)',
    detail:
      'Featured panel guest discussing equal parenting, parental alienation, due process, and legislative solutions.',
  },
  {
    title: 'The Present Father Show (2026)',
    detail:
      'Featured guest discussing the transition from direct family court litigation to national legislative reform.',
  },
  {
    title: 'Stakelbeck Tonight: Trinity Broadcasting Network (2025)',
    detail:
      'National television interview on parental rights, judicial accountability, custody interference, and parental alienation.',
  },
  {
    title: 'Dr. Phil (2024)',
    detail: 'Television appearance as featured advocate on family court reform and parental rights.',
  },
  {
    title: 'Raised by These Wolves, Volume II: Chapter 21 (2024)',
    detail: 'Contributing author. Focus on family court reform and parental rights.',
  },
  {
    title: 'The Epoch Times (2023, 2024)',
    detail: 'Featured articles on Time Taken Time Back legislation and family court reform.',
  },
  {
    title: 'Family Courts in the Firing Line, Bettina Arndt (2025)',
    detail: 'Featured contributor discussing judicial accountability and parental rights.',
  },
  {
    title: 'The Global Movement to End Parental Alienation: Severed Voices (2025)',
    detail: 'Featured contributor on the international movement to end parental alienation.',
  },
  {
    title: 'Slam the Gavel, Season 5',
    detail: 'Podcast guest discussing family court reform and judicial accountability.',
  },
];

const leadershipPositions = [
  {
    title: 'Founder, Owner & CEO, RobertGarza.us',
    detail:
      'Leading a national and international platform providing free access to reform legislation, state-specific proposals, and advocacy tools',
  },
  {
    title: 'Executive Director, Americans for Judicial Accountability (AJA)',
    detail: 'Leading global efforts for judicial transparency and accountability (July 2025 - Present)',
  },
  {
    title: 'Director, Texans for Judicial Accountability',
    detail:
      'Directing state-level initiatives promoting transparency, accountability, and reform within the Texas judiciary',
  },
  {
    title: 'Board Member, The John Mast Foundation',
    detail:
      'Governance oversight and strategic guidance for initiatives supporting parental rights, family preservation, and due process reforms',
  },
];

const affiliations = [
  {
    title: 'Advisory Council Member, National Parents Organization',
    detail:
      "Supporting national shared-parenting policies that preserve children's relationships with both fit parents",
  },
  {
    title: 'Member, Parental Alienation Study Group (PASG)',
    detail: 'Contributing to research and awareness of parental alienation issues',
  },
  {
    title: 'Former Vice President, National Family Justice',
    detail: 'Provided national family court reform and coalition leadership from August 2025 through April 19, 2026',
  },
];

const internationalReformScope = [
  'Due Process Protections',
  'Custody Enforcement Mechanisms',
  'Court Transparency Measures',
  'Judicial Accountability Standards',
  'Parental Alienation Prevention',
  'CPS Investigation & Due Process Reform',
  'Guardian ad Litem & Court-Appointed Professional Oversight',
  'Child Support & Best-Interest Standards',
];

const federalInternationalWork = [
  'Federal TTTB (Time Taken, Time Back) Efforts',
  'International Legislative Drafting',
  'Cross-Border Family Court Standards',
  'International Advocacy Networks',
  'Global Best Practices Research',
  '50-State Legislative Toolkit: 12 Model Bills Per State',
  'Canada, Australia & Europe Legislative Development',
];

const skills = [
  'Legislative Drafting',
  'Statutory Analysis',
  'Public Policy Development',
  'Judicial Accountability',
  'Parental Rights',
  'Parenting-Time Enforcement',
  'CPS Reform',
  'Guardian ad Litem Reform',
  'Trial & Appellate Strategy',
  'Public Speaking & Testimony',
  'Media Relations',
  'International Reform',
  'Coalition Building',
  'Data-Driven Policy Analysis',
  'Grassroots Organizing',
];

const reformBills = [
  {
    image: CustodyImage,
    title: '50/50 Custody Presumption',
    alt: 'Illustration for the 50/50 custody presumption reform bill',
    slug: 'a-new-start-at-equality',
    path: '/bill/a-new-start-at-equality',
  },
  {
    image: TimeTakenTimeBackImage,
    title: 'Time Taken, Time Back',
    alt: 'Illustration for the Time Taken, Time Back reform bill',
    slug: 'time-taken-time-back',
    path: '/bill/time-taken-time-back',
  },
  {
    image: ThreeStrikesImage,
    title: 'The Three Strikes Law',
    alt: 'Illustration for the three strikes interference reform bill',
    slug: 'three-strikes',
    path: '/bill/three-strikes',
  },
  {
    image: CamerasImage,
    title: 'Cameras in Courtroom',
    alt: 'Illustration for the cameras in courtrooms reform bill',
    slug: 'cameras-in-courtrooms',
    path: '/cameras-in-courtroom-law',
  },
  {
    image: AppealableOrdersImage,
    title: 'Appealable Temporary Orders',
    alt: 'Illustration for the appealable temporary orders reform bill',
    slug: 'appealable-temporary-orders',
    path: '/bill/appealable-temporary-orders',
  },
  {
    image: ChildSupportImage,
    title: 'Child Support Reform',
    alt: 'Illustration for the child support dual calculation reform bill',
    slug: 'child-support-dual-calculation-reform',
    path: '/bill/child-support-dual-calculation-reform',
  },
];

const impactStats = [
  { value: '600+', label: 'Reform bills drafted' },
  { value: '3', label: 'Laws enacted in Texas and Utah' },
  { value: '50', label: 'U.S. states covered by the model toolkit' },
  { value: '17', label: 'Years of courtroom experience' },
];

const AtAGlanceIcon = StarIcon;

const About: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentColor = isDark ? ACCENT_DARK : ACCENT_LIGHT;
  // Theme background color with alpha: opacity fades adapt to light/dark mode instead of a hardcoded black scrim
  const bg = theme.palette.background.default;
  const bgAlpha = (opacityPercent: number) => `color-mix(in srgb, ${bg} ${opacityPercent}%, transparent)`;
  const navigate = useNavigate();
  const [joinTeamDialogOpen, setJoinTeamDialogOpen] = useState(false);
  const [calendlyDialogOpen, setCalendlyDialogOpen] = useState(false);

  const handleJoinTeamClick = () => {
    trackEvent('cta_click', 'engagement', 'Join the Movement - About');
    setJoinTeamDialogOpen(true);
  };

  const handleJoinTeamDialogClose = () => {
    setJoinTeamDialogOpen(false);
  };

  const handleCalendlyClick = () => {
    trackScheduleCall('About');
    setCalendlyDialogOpen(true);
  };

  const handleCalendlyDialogClose = () => {
    setCalendlyDialogOpen(false);
  };

  const focusRing = {
    '&:focus-visible': {
      outline: '3px solid',
      outlineColor: accentColor,
      outlineOffset: '2px',
    },
  };

  return (
    <Container component="main" maxWidth={false}>
      {/* Respect users who prefer reduced motion: collapse all transitions and animations */}
      <GlobalStyles
        styles={{
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              transitionDuration: '0.01ms !important',
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              scrollBehavior: 'auto !important',
            },
          },
        }}
      />

      <SEO
        title="About Robert Garza | Family Court Reform Advocate & Author"
        description="Meet Robert Garza, a national and international family court reform advocate and author of 600+ bills advancing parental rights, due process & accountability."
        path="/about-robert-garza"
        schema={generateWebPageSchema({
          name: 'About Robert Garza | Family Court Reform Advocate & Author',
          description:
            'Meet Robert Garza, a national and international family court reform advocate and author of 600+ bills advancing parental rights, due process & accountability.',
          url: '/about-robert-garza',
        })}
      />

      {/* Hero: asymmetric split. Text over an opaque-to-transparent page-background gradient (left to right),
          theme-adaptive in both modes; the photo scales to the hero height and anchors right so the subject
          stays visible beside the text instead of sliding under it. */}
      <Box
        component="section"
        aria-labelledby="about-hero-heading"
        sx={{
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          marginX: { xs: -2, sm: -3 },
          px: { xs: 3, sm: 6, md: 10 },
          py: { xs: 10, md: 14 },
          backgroundImage: {
            xs: `linear-gradient(90deg, ${bgAlpha(92)} 0%, ${bgAlpha(78)} 52%, ${bgAlpha(42)} 100%), url(${HeroImage})`,
            md: `linear-gradient(90deg, ${bg} 0%, ${bg} 34%, ${bgAlpha(62)} 56%, ${bgAlpha(22)} 76%, transparent 100%), url(${HeroImage})`,
          },
          backgroundPosition: { xs: '24% top', md: 'right center' },
          backgroundSize: { xs: 'cover', md: 'auto 100%' },
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box sx={{ maxWidth: { xs: '100%', md: '640px' } }}>
          <Typography
            id="about-hero-heading"
            variant="h1"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.02em', color: 'text.primary' }}
          >
            National &amp; International Family Court Reform Advocate
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              color: accentColor,
              fontSize: { xs: '1.1rem', md: '1.35rem' },
              mb: 3,
            }}
          >
            Robert Garza
          </Typography>
          <Typography
            variant="h3"
            component="p"
            sx={{
              fontWeight: 300,
              lineHeight: 1.5,
              mb: 4,
              maxWidth: '65ch',
              color: 'text.primary',
            }}
          >
            From the courtroom to the Capitol: practical family court reform that protects children and preserves
            families.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'stretch' }}>
            <Button
              variant="contained"
              size="large"
              href="/family-court-reform-bills"
              sx={{ px: 4, py: 2, borderRadius: 999, ...focusRing }}
            >
              View Reform Bills
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleCalendlyClick}
              sx={{
                px: 4,
                py: 2,
                borderRadius: 999,
                ...focusRing,
                color: 'text.primary',
                borderColor: 'divider',
              }}
            >
              Schedule with Robert
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Impact stat band */}
      <Box
        component="section"
        aria-label="Legislative impact at a glance"
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          marginX: { xs: -2, sm: -3 },
          backgroundColor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[100]),
          backgroundImage: (t) =>
            t.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.06) 0%, rgba(255, 215, 0, 0.02) 100%)'
              : 'linear-gradient(135deg, rgba(184, 134, 11, 0.10) 0%, rgba(184, 134, 11, 0.04) 100%)',
        }}
      >
        <Container>
          <Grid container spacing={{ xs: 4, md: 2 }}>
            {impactStats.map((stat) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography
                    component="p"
                    sx={{ fontSize: { xs: '2.5rem', md: '3rem' }, fontWeight: 800, color: accentColor, lineHeight: 1 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Biography */}
      <Box component="section" aria-labelledby="about-bio-heading" sx={{ py: { xs: 8, md: 14 }, px: { xs: 1, md: 4 } }}>
        <Container>
          <Typography
            id="about-bio-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            About Robert Garza
          </Typography>

          <Grid container spacing={6}>
            <Grid item xs={12} md={8}>
              <Typography paragraph sx={{ fontSize: '1.2rem', lineHeight: 1.8 }}>
                Robert Garza is a national and international family court reform advocate, legislative author,
                solution-builder, and public speaker. He turned 17 years of high-conflict family court litigation, more
                than 43 CPS allegations, and over $700,000 in legal costs into a public legislative platform focused on
                protecting children, preserving families, and strengthening due process.
              </Typography>

              <Typography paragraph sx={{ fontSize: '1.2rem', lineHeight: 1.8 }}>
                Robert is the original author and driving force behind <strong>Texas SB 718</strong>, the Time Taken
                Time Back law signed on Father&apos;s Day 2023. He also developed and advocated for the Three Strikes
                reform and helped advance <strong>Texas HB 3181</strong>, which strengthened remedies and escalating
                consequences for repeated interference with court-ordered possession and access effective September 1,
                2025.
              </Typography>

              <Typography paragraph sx={{ fontSize: '1.2rem', lineHeight: 1.8 }}>
                His work now includes{' '}
                <strong>more than 600 national and international family court reform bills</strong>, including 12
                comprehensive model reform bills for each of the 50 U.S. states. He has also drafted proposals under
                consideration by advocates and policymakers in Canada, Australia, and Europe, covering equal parenting,
                enforcement, CPS and guardian ad litem reform, court transparency, child support, access to justice,
                judicial accountability, and family preservation.
              </Typography>

              <Typography paragraph sx={{ fontSize: '1.2rem', lineHeight: 1.8, mb: 0 }}>
                In 2026, Robert helped advance Utah&apos;s Time Taken Time Back reform, signed by the Governor on March
                23, 2026. He testified before the <strong>Texas Commission on Marriage and Family</strong>, delivered
                keynote presentations in Sofia, Bulgaria and Salt Lake City, and spoke at the New York Shared Parenting
                Conference. His work was recognized with a Westchester County proclamation and the Voices of Hope
                Humanitarian Award.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 3,
                  backgroundColor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[800] : t.palette.grey[100]),
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <AtAGlanceIcon aria-hidden="true" sx={{ fontSize: 44, color: accentColor, mb: 2 }} />
                <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
                  At a Glance
                </Typography>
                <List disablePadding dense>
                  {[
                    'Founder, Owner & CEO of RobertGarza.us',
                    'Executive Director, Americans for Judicial Accountability',
                    'Author of more than 600 reform bills',
                    'Voices of Hope Humanitarian Award recipient',
                  ].map((item, index, arr) => (
                    <ListItem key={item} disableGutters divider={index < arr.length - 1}>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body1', fontWeight: 600 }} />
                    </ListItem>
                  ))}
                </List>
                <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 3 }}>
                  Learn more about Robert&apos;s work:{' '}
                  <Link
                    href="https://www.RobertGarza.us"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: accentColor,
                      fontWeight: 'bold',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                      ...focusRing,
                    }}
                  >
                    www.RobertGarza.us
                  </Link>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Key Accomplishments: bento grid with varied tile treatments */}
      <Box
        component="section"
        aria-labelledby="about-accomplishments-heading"
        sx={{ py: { xs: 8, md: 14 }, px: { xs: 1, md: 4 } }}
      >
        <Container>
          <Typography
            id="about-accomplishments-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 6, textAlign: 'center' }}
          >
            Key Accomplishments
          </Typography>

          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' } }}>
            {[
              {
                span: 7,
                gradient: true,
                icon: <GavelIcon aria-hidden="true" sx={{ fontSize: 48, color: accentColor, mb: 2 }} />,
                title: 'Texas SB 718: Time Taken Time Back',
                body: 'Original author and driving force behind the law restoring qualifying parenting time after denial of court-ordered possession or access. Signed on Father\u2019s Day 2023.',
              },
              {
                span: 5,
                gradient: false,
                icon: <CampaignIcon aria-hidden="true" sx={{ fontSize: 48, color: accentColor, mb: 2 }} />,
                title: 'Texas HB 3181: Three Strikes Reform',
                body: 'Escalating remedies for repeated interference with parenting time. Passed the Texas House 135-6 and Senate 31-0; effective September 1, 2025.',
              },
              {
                span: 5,
                gradient: false,
                icon: <PublicIcon aria-hidden="true" sx={{ fontSize: 48, color: accentColor, mb: 2 }} />,
                title: 'Utah Time Taken Time Back',
                body: 'Helped advance Utah legislation based on the Time Taken Time Back model, signed by the Governor on March 23, 2026.',
              },
              {
                span: 7,
                gradient: false,
                icon: <AccountBalanceIcon aria-hidden="true" sx={{ fontSize: 48, color: accentColor, mb: 2 }} />,
                title: 'Texas Proposition 12 (2025-2026)',
                body: 'Participated in advocacy surrounding SJR 27, the voter-approved judicial-accountability reform that restructured the State Commission on Judicial Conduct effective January 1, 2026.',
              },
              {
                span: 4,
                gradient: true,
                icon: <AccountBalanceIcon aria-hidden="true" sx={{ fontSize: 48, color: accentColor, mb: 2 }} />,
                title: '600+ Bills Worldwide',
                body: 'More than 600 proposals, including 12 comprehensive model reform bills for every U.S. state and legislation for international jurisdictions.',
              },
              {
                span: 4,
                gradient: false,
                icon: <BalanceIcon aria-hidden="true" sx={{ fontSize: 48, color: accentColor, mb: 2 }} />,
                title: 'AJA Executive Director (July 2025)',
                body: 'Appointed Executive Director of Americans for Judicial Accountability, leading global reform efforts.',
              },
              {
                span: 4,
                gradient: false,
                icon: <MicIcon aria-hidden="true" sx={{ fontSize: 48, color: accentColor, mb: 2 }} />,
                title: '2026 National & International Expansion',
                body: 'Testimony before the Texas Commission on Marriage and Family plus keynote presentations in Sofia, Bulgaria and Salt Lake City.',
              },
            ].map((tile) => (
              <Box
                key={tile.title}
                sx={{
                  gridColumn: { xs: 'span 1', md: `span ${tile.span}` },
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: (t) => (tile.gradient ? undefined : t.palette.background.paper),
                  backgroundImage: tile.gradient
                    ? (t) =>
                        t.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.16) 0%, rgba(255, 215, 0, 0.04) 100%)'
                          : 'linear-gradient(135deg, rgba(255, 215, 0, 0.20) 0%, rgba(184, 134, 11, 0.08) 100%)'
                    : undefined,
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                {tile.icon}
                <Typography variant="h3" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {tile.title}
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  {tile.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Leadership Roles: two bordered columns, divider between rows only */}
      <Box
        component="section"
        aria-labelledby="about-leadership-heading"
        sx={{ py: { xs: 8, md: 14 }, px: { xs: 1, md: 4 } }}
      >
        <Container>
          <Typography
            id="about-leadership-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 6, textAlign: 'center' }}
          >
            Leadership Roles
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h3" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                  Current Leadership Positions
                </Typography>
                <List disablePadding>
                  {leadershipPositions.map((role, index, arr) => (
                    <ListItem
                      key={role.title}
                      divider={index < arr.length - 1}
                      disableGutters
                      sx={{ alignItems: 'flex-start' }}
                    >
                      <ListItemText
                        primary={role.title}
                        secondary={role.detail}
                        primaryTypographyProps={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h3" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                  Professional Affiliations
                </Typography>
                <List disablePadding>
                  {affiliations.map((role, index, arr) => (
                    <ListItem
                      key={role.title}
                      divider={index < arr.length - 1}
                      disableGutters
                      sx={{ alignItems: 'flex-start' }}
                    >
                      <ListItemText
                        primary={role.title}
                        secondary={role.detail}
                        primaryTypographyProps={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Speaking engagements: scroll-snap rail, keyboard reachable */}
      <Box
        component="section"
        aria-labelledby="about-speaking-heading"
        sx={{ py: { xs: 8, md: 14 }, px: { xs: 1, md: 4 } }}
      >
        <Container>
          <Typography
            id="about-speaking-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 6 }}
          >
            Speaking Engagements
          </Typography>
        </Container>
        <Container
          component="div"
          role="region"
          aria-label="Featured speaking engagements, scroll horizontally"
          tabIndex={0}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 2,
            scrollSnapType: 'x proximity',
            ...focusRing,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 999 },
          }}
        >
          {speakingEngagements.map((engagement) => (
            <Box
              key={engagement.event}
              component="article"
              sx={{
                flex: '0 0 auto',
                scrollSnapAlign: 'start',
                width: { xs: 260, sm: 300 },
                p: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[800] : t.palette.grey[100]),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: accentColor, mb: 1 }}>
                {engagement.role}
              </Typography>
              <Typography sx={{ fontWeight: 'bold', mb: 1, lineHeight: 1.4 }}>{engagement.event}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {engagement.date}
              </Typography>
            </Box>
          ))}
        </Container>
      </Box>

      {/* Publications & Media: compact card grid */}
      <Box
        component="section"
        aria-labelledby="about-publications-heading"
        sx={{ py: { xs: 8, md: 14 }, px: { xs: 1, md: 4 } }}
      >
        <Container>
          <Typography
            id="about-publications-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 6 }}
          >
            Publications &amp; Media
          </Typography>

          <Grid container spacing={3}>
            {publications.map((publication) => (
              <Grid item xs={12} sm={6} key={publication.title}>
                <Box
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '4px solid',
                    borderLeftColor: accentColor,
                  }}
                >
                  <Typography component="h3" sx={{ fontWeight: 'bold', mb: 1, lineHeight: 1.4 }}>
                    {publication.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {publication.detail}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Legislative Advocacy: two icon-list cards */}
      <Box
        component="section"
        aria-labelledby="about-advocacy-heading"
        sx={{ py: { xs: 8, md: 14 }, px: { xs: 1, md: 4 } }}
      >
        <Container>
          <Typography
            id="about-advocacy-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 6, textAlign: 'center' }}
          >
            Legislative Advocacy
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography variant="h3" component="h3" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                    International Reform Scope
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.6 }}>
                    Robert&apos;s legislative work spans multiple countries, addressing critical areas including:
                  </Typography>
                  <List dense disablePadding>
                    {internationalReformScope.map((item) => (
                      <ListItem key={item} disableGutters>
                        <Box
                          component="span"
                          aria-hidden="true"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: accentColor,
                            mr: 2,
                            flexShrink: 0,
                          }}
                        />
                        <ListItemText primary={item} primaryTypographyProps={{ variant: 'body1' }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography variant="h3" component="h3" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                    Federal &amp; International Work
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.6 }}>
                    Beyond state-level reform, Robert&apos;s influence extends to:
                  </Typography>
                  <List dense disablePadding>
                    {federalInternationalWork.map((item) => (
                      <ListItem key={item} disableGutters>
                        <Box
                          component="span"
                          aria-hidden="true"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: accentColor,
                            mr: 2,
                            flexShrink: 0,
                          }}
                        />
                        <ListItemText primary={item} primaryTypographyProps={{ variant: 'body1' }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Skills & Expertise: chip cloud */}
      <Box
        component="section"
        aria-labelledby="about-skills-heading"
        sx={{ py: { xs: 8, md: 14 }, px: { xs: 1, md: 4 } }}
      >
        <Container>
          <Typography
            id="about-skills-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 6, textAlign: 'center' }}
          >
            Skills &amp; Expertise
          </Typography>

          <Stack direction="row" flexWrap="wrap" justifyContent="center" sx={{ gap: 2 }}>
            {skills.map((skill) => (
              <Chip
                key={skill}
                label={skill}
                sx={{
                  fontSize: '1rem',
                  py: 2.5,
                  px: 2,
                  fontWeight: 600,
                  borderRadius: 999,
                  color: 'text.primary',
                  backgroundColor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[800] : t.palette.grey[200]),
                }}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Media & Organizations: documentary image cards + memberships (full-bleed band) */}
      <Box
        component="section"
        aria-labelledby="about-media-heading"
        sx={{
          py: { xs: 8, md: 14 },
          px: { xs: 3, md: 6 },
          marginX: { xs: -2, sm: -3 },
          backgroundColor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[100]),
        }}
      >
        <Container>
          <Typography
            id="about-media-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 6, textAlign: 'center' }}
          >
            Media &amp; Organizations
          </Typography>

          <Grid container spacing={{ xs: 4, md: 6 }} sx={{ mb: 10 }}>
            {[
              {
                image: WeTheParentsImage,
                alt: 'Poster for the We the Parents documentary, opens on YouTube',
                title: 'We the Parents',
                body: 'Featured advocate in this documentary exploring parental rights',
                href: 'https://www.youtube.com/watch?v=dTxqEFTBgJM',
                label: 'We the Parents Documentary - YouTube',
              },
              {
                image: IStandWithJohnImage,
                alt: 'Poster for the I Stand with John documentary, opens on YouTube',
                title: 'I Stand with John',
                body: 'Executive producer of this documentary on family court reform, over 2.1 million YouTube views',
                href: 'https://youtu.be/EYl3SjG-gCc?si=nsRvDcub8kU1Jyay',
                label: 'I Stand with John Documentary - YouTube',
              },
            ].map((doc) => (
              <Grid item xs={12} md={6} key={doc.title}>
                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    href={doc.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${doc.title}, watch on YouTube`}
                    onClick={() => trackEvent('external_link_click', 'video', doc.label)}
                    sx={{
                      position: 'relative',
                      display: 'block',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: 3,
                      mb: 3,
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': { transform: 'translateY(-4px)' },
                      ...focusRing,
                    }}
                  >
                    <Box
                      component="img"
                      src={doc.image}
                      alt={doc.alt}
                      loading="lazy"
                      sx={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                    <Box
                      aria-hidden="true"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 72,
                          height: 72,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(0, 0, 0, 0.55)',
                          border: '2px solid rgba(255, 255, 255, 0.85)',
                        }}
                      >
                        <PlayArrowIcon sx={{ color: '#FFFFFF', fontSize: 40 }} />
                      </Box>
                    </Box>
                  </Link>
                  <Typography variant="h3" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {doc.title}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    {doc.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h3" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 5, textAlign: 'center' }}>
            Vital Memberships
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {[
              {
                image: AffiliateLogoOne,
                alt: 'Texans for Judicial Accountability logo',
                name: 'Texans for Judicial Accountability',
              },
              {
                image: AffiliateLogoTwo,
                alt: 'National Parents Organization logo',
                name: 'National Parents Organization',
              },
              { image: AffiliateLogoThree, alt: 'The John Mast Foundation logo', name: 'The John Mast Foundation' },
            ].map((org) => (
              <Grid item xs={12} sm={4} key={org.name} sx={{ textAlign: 'center' }}>
                <Box
                  component="img"
                  src={org.image}
                  alt={org.alt}
                  loading="lazy"
                  sx={{ mb: 2, maxHeight: 72, maxWidth: 200, width: 'auto', height: 'auto' }}
                />
                <Typography variant="body2">{org.name}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Reform bills grid */}
      <Box
        component="section"
        aria-labelledby="about-bills-heading"
        sx={{ py: { xs: 8, md: 14 }, px: { xs: 1, md: 4 } }}
      >
        <Container>
          <Typography
            id="about-bills-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}
          >
            Be Part of the Solution
          </Typography>

          <Typography
            variant="body1"
            sx={{ fontSize: '1.2rem', lineHeight: 1.8, mb: 3, textAlign: 'center', maxWidth: '65ch', mx: 'auto' }}
          >
            Support for the Family Court Reform Bill Stack is growing, driven by grassroots advocacy and public demand
            for justice.
          </Typography>

          <Typography
            variant="body1"
            sx={{ fontSize: '1.2rem', lineHeight: 1.8, mb: 6, textAlign: 'center', maxWidth: '65ch', mx: 'auto' }}
          >
            Take action now to support these critical reforms. Learn more about each bill, share the movement, and help
            fix family courts worldwide.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              mb: 6,
            }}
          >
            {reformBills.map((bill) => (
              <Card
                key={bill.slug}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 },
                }}
              >
                <CardActionArea
                  onClick={() => {
                    trackEvent('bill_card_click', 'bills', bill.slug);
                    navigate(bill.path);
                  }}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', ...focusRing }}
                >
                  <Box
                    component="img"
                    src={bill.image}
                    alt={bill.alt}
                    loading="lazy"
                    sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                  />
                  <CardContent
                    sx={{
                      textAlign: 'center',
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {bill.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: accentColor, fontWeight: 'bold' }}>
                      Read the bill
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: 1.8, mb: 4 }}>
              Together, we can build a system that puts children first and upholds justice for all families.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={handleJoinTeamClick}
                startIcon={<PersonAddIcon />}
                sx={{ px: 4, py: 2, borderRadius: 999, ...focusRing }}
              >
                Join the Movement
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="/family-court-reform-bills"
                sx={{ px: 4, py: 2, borderRadius: 999, ...focusRing }}
              >
                View Reform Bills
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Get Involved: final CTA band (full-bleed) */}
      <Box
        component="section"
        aria-labelledby="about-get-involved-heading"
        sx={{
          py: { xs: 8, md: 14 },
          px: { xs: 3, md: 6 },
          marginX: { xs: -2, sm: -3 },
          backgroundColor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[100]),
          backgroundImage: (t) =>
            t.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.08) 0%, rgba(255, 215, 0, 0.02) 100%)'
              : 'linear-gradient(135deg, rgba(255, 215, 0, 0.16) 0%, rgba(184, 134, 11, 0.06) 100%)',
        }}
      >
        <Container>
          <Typography
            id="about-get-involved-heading"
            variant="h2"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}
          >
            Get Involved
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: '1.2rem',
              lineHeight: 1.8,
              mb: 6,
              textAlign: 'center',
              maxWidth: '65ch',
              mx: 'auto',
              color: 'text.secondary',
            }}
          >
            Join Robert in the fight for family justice. Whether you need guidance, want to schedule a personal case
            review, or wish to collaborate on reform efforts, take action today.
          </Typography>

          <Card
            sx={{
              maxWidth: 720,
              mx: 'auto',
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              boxShadow: 3,
              backgroundColor: (t) => t.palette.background.paper,
            }}
          >
            <EventIcon aria-hidden="true" sx={{ fontSize: '3rem', color: accentColor, mb: 2 }} />
            <Typography variant="h3" component="h3" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
              Schedule a Personal Case Review
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.6, maxWidth: '55ch', mx: 'auto' }}>
              Meet with Robert one-on-one over video to talk through your custody, visitation, or court reform situation
              and leave with clear, practical next steps.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleCalendlyClick}
              sx={{ fontWeight: 'bold', px: 4, py: 2, borderRadius: 999, ...focusRing }}
            >
              Schedule with Robert
            </Button>
          </Card>
        </Container>
      </Box>

      {/* Join The Team Dialog */}
      <JoinTheTeamDialog open={joinTeamDialogOpen} onClose={handleJoinTeamDialogClose} />

      {/* Calendly Dialog */}
      {calendlyDialogOpen && (
        <React.Suspense fallback={<div />}>
          <CalendlyDialog
            open={calendlyDialogOpen}
            onClose={handleCalendlyDialogClose}
            calendlyUrl="https://calendly.com/robert-garza-us"
          />
        </React.Suspense>
      )}
    </Container>
  );
};

export default About;
